import { OpsiumUserShortResponse } from '@/modules/common';
import { CurrencyDTO } from '@/modules/common/types/dto/currency.dto';
import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';
import { AttributeValueResponse } from '@/modules/common/types/dto/response/custom-attribute-response';

import { ProjectBillingModelDTO } from './project-billing-model.dto';
import { ProjectStatusDTO } from './project-status.dto';
import { UserRateOnProjectResponse } from './user-rate-on-project.dto';

/**
 * DTO enum for patch entity effect actions
 */
export enum PatchEntityEffectActionDTO {
  DELETE = 'DELETE',
  CUT = 'CUT',
}

export type PatchEntityEffectActionRequest = PatchEntityEffectActionDTO;
export type PatchEntityEffectActionResponse = PatchEntityEffectActionDTO;

/**
 * DTO interface for patch entity effect
 * Describes the impact of a patch operation on an existing entity change
 */
export interface PatchEntityEffectDTO {
  /**
   * Action that will be taken on the existing change
   * DELETE: Existing change will be completely removed
   * CUT: Existing change will be shortened/split
   */
  action: PatchEntityEffectActionDTO;

  /**
   * New start date if the change is being cut
   * Only populated when action is CUT
   */
  cutEffectiveStartDate: string | null;

  /**
   * New end date if the change is being cut
   * Only populated when action is CUT
   */
  cutEffectiveEndDate: string | null;
}

/**
 * DTO interface for project info versioned fields
 * Contains the fields that change over time
 */
export interface ProjectInfoVersionedFieldsResponseDTO {
  status: ProjectStatusDTO;
  tentativeProbability: number | null;
  managers: OpsiumUserShortResponse[];
  projectStartDate: string | null;
  projectEndDate: string | null;
  industries: string[] | null;
  customAttributesValues: AttributeValueResponse[];
}

/**
 * DTO interface for a single project info snapshot
 * Represents the state of project info at a specific time period
 */
export interface ProjectInfoSnapshotResponseDTO {
  /**
   * The actual project info data for this time period
   */
  snapshot: ProjectInfoVersionedFieldsResponseDTO;

  /**
   * Describes what will happen to this snapshot if the new change is applied
   */
  patchEffect: PatchEntityEffectDTO;

  /**
   * When this snapshot becomes effective
   */
  effectiveStartDate: string;

  /**
   * When this snapshot ends (null means infinite/no end date)
   */
  effectiveEndDate: string | null;
}

/**
 * DTO interface for list of project info snapshots
 * Response from GET /api/projects/info
 */
export interface ProjectInfoListResponseDTO {
  items: ProjectInfoSnapshotResponseDTO[];
}

/**
 * DTO interface for project billing snapshot data
 * Contains the billing configuration at a specific time
 */
export interface ProjectBillingResponseDTO {
  billingModel: ProjectBillingModelDTO | null;
  defaultCurrency: CurrencyDTO;
  rateSource: RateSourceDTO;
  flatRate: number | null;
  budget: number | null;
  userRates: UserRateOnProjectResponse[];
  hasAnyDeletedRelations: boolean;
}

/**
 * DTO interface for a single project billing snapshot
 * Represents the state of project billing at a specific time period
 */
export interface ProjectBillingSnapshotResponseDTO {
  /**
   * The actual project billing data for this time period
   */
  snapshot: ProjectBillingResponseDTO;

  /**
   * Describes what will happen to this snapshot if the new change is applied
   */
  patchEffect: PatchEntityEffectDTO;

  /**
   * When this snapshot becomes effective
   */
  effectiveStartDate: string;

  /**
   * When this snapshot ends (null means infinite/no end date)
   */
  effectiveEndDate: string | null;
}

/**
 * DTO interface for list of project billing snapshots
 * Response from GET /api/projects/billing
 */
export interface ProjectBillingListResponseDTO {
  items: ProjectBillingSnapshotResponseDTO[];
}
